<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import type { FileDTO } from '@/composables/media/media'
import { getDefaultAvatarId } from '@/utils/config'

const { t } = useI18n()
const { width } = useWindowSize()
const dialogWidth = computed(() => (width.value < 768 ? '90%' : '450px'))
const visible = defineModel<boolean>()
const emit = defineEmits(['useExistingAvatar', 'uploadNewImage'])

const props = defineProps({
  avatar: { type: Object as PropType<FileDTO>, required: false, default: null },
  blob: { type: Object as PropType<Blob>, required: false, default: null }
})
const imageThumbUrl = computed(() => {
  return props.blob ? URL.createObjectURL(props.blob) : undefined
})

const validAvatar = computed(() => {
  if (!props.avatar) return undefined
  // Check if it's the default avatar ID
  if (props.avatar.id === getDefaultAvatarId()) {
    return undefined
  }
  const name = props.avatar.name?.toLowerCase() || ''
  const url = props.avatar.url?.toLowerCase() || ''
  // If name or url contains 'demo', treat it as no avatar (default avatar)
  if (name.includes('demo') || url.includes('demo')) {
    return undefined
  }
  return props.avatar
})

const handleUseCurrentImageClick = () => {
  visible.value = false
  emit('useExistingAvatar', validAvatar.value)
}

const handleUploadNewImageClick = () => {
  visible.value = false
  emit('uploadNewImage')
}
</script>
<template>
  <el-dialog
    v-model="visible"
    :width="dialogWidth"
    :show-close="true"
    p-0
    pb-4
    style="background: linear-gradient(135deg, #ffffff 0%, #fef7ef 35%, #ffffff 100%)">
    <div w-full text-center text-main text-2xl font-bold font-serif-tc>
      {{ t('portrait.select_photo_for_beautify') }}
    </div>
    <div text-base text-main mt-5 mx-6 leading-7>
      {{ t('portrait.upload_desc') }}
    </div>

    <div flex items-start justify-center w-full mt-5 mb-8>
      <div
        flex-1
        border-r-1
        border-gray-200
        v-if="validAvatar || imageThumbUrl"
        flex
        flex-col
        items-center
        justify-center>
        <img :src="validAvatar?.middleQuality || imageThumbUrl" alt="" w-27 rounded-2 bg-gray class="aspect-[3/4]" />
        <div
          @click="handleUseCurrentImageClick"
          mt-3
          bg-portrait-green
          btn-solid
          text-center>
          {{ t('portrait.use_existing_avatar') }}
        </div>
      </div>
      <div flex-1 flex flex-col items-center justify-center>
        <div
          @click="handleUploadNewImageClick"
          flex
          flex-col
          items-center
          justify-center
          border-2
          border-dashed
          border-gray-500
          cursor-pointer
          rounded-2
          w-27
          h-36
          bg-portrait-light-brown>
          <div class="i-ic:sharp-upload" style="color: #e7c3a0" w-15 h-15></div>
        </div>
        <div text-main text-base mt-4>
          {{ validAvatar || imageThumbUrl ? t('portrait.upload_new_image1') : t('portrait.upload_new_image2') }}
        </div>
      </div>
    </div>
  </el-dialog>
</template>
<style scoped></style>
